import { getExtensionValue, resolveId } from "@medplum/core";
import { Condition, Procedure } from "@medplum/fhirtypes";

import { Extensions } from "@/fhir/enums/extensions";
import { getCodeableConceptCode, getCodeableConceptDisplay } from "@/utils/medplum";

import { PROCEDURES_ENTERED_IN_ERROR } from "../constants/procedures";
import {
  EncounterProceduresVersionChains,
  ProceduresAmendmentChange,
  ProceduresAmendmentProcedureValues,
  ProceduresAmendmentRow,
  ProceduresAmendmentWindow,
} from "../types/procedures";

import { readProcedureNoteText } from "./procedure-notes";
import {
  pickProceduresAmendmentVersionPair,
  toProceduresAmendmentBoundaries,
} from "./procedures-amendment-window";

const BLANK_VALUES: ProceduresAmendmentProcedureValues = {
  code: "",
  units: "",
  modifiers: "",
  providerNotes: "",
  billerNotes: "",
  diagnoses: "",
};

const isRetiredProcedure = (procedure: Procedure): boolean =>
  procedure.status === PROCEDURES_ENTERED_IN_ERROR;

const isRetiredCondition = (condition: Condition): boolean =>
  Boolean(
    condition.verificationStatus?.coding?.some(
      (coding) => coding.code === PROCEDURES_ENTERED_IN_ERROR,
    ),
  );

/** "99213 - Office visit", or the code alone when the resource carries no display of its own. */
const toCodeLabel = (resource: Procedure | Condition): string => {
  const code = getCodeableConceptCode(resource.code);
  const display = getCodeableConceptDisplay(resource.code);

  if (!code) return display;

  return display && display !== code ? `${code} - ${display}` : code;
};

/**
 * Indexes the diagnoses that were live on one side of the amendment, by resource id.
 *
 * A procedure points at its diagnoses by reference, so the labels one side shows have to come from
 * the diagnoses as that same side recorded them. A diagnosis retired on this side is left out, which
 * is what takes a removed diagnosis off the procedures still pointing at it.
 *
 * @param conditions - Each diagnosis as this side recorded it, or undefined where it did not exist.
 * @returns The live diagnoses on this side, keyed by id.
 */
const toLiveConditionsById = (conditions: (Condition | undefined)[]): Map<string, Condition> => {
  const conditionsById = new Map<string, Condition>();

  for (const condition of conditions) {
    if (condition?.id && !isRetiredCondition(condition)) {
      conditionsById.set(condition.id, condition);
    }
  }

  return conditionsById;
};

/**
 * Deduplicated by code, matching the procedures tab's established rendering: a procedure linked to
 * two Condition resources sharing a code would otherwise show it twice and read as an unrelated
 * change to the diagnoses text.
 */
const toDiagnosesLabel = (
  procedure: Procedure,
  liveConditionsById: ReadonlyMap<string, Condition>,
): string => {
  const seenCodes = new Set<string>();
  const labels: string[] = [];

  for (const reference of procedure.reasonReference ?? []) {
    const conditionId = resolveId(reference);
    const condition = conditionId ? liveConditionsById.get(conditionId) : undefined;
    if (!condition) continue;

    const code = getCodeableConceptCode(condition.code);
    if (code) {
      if (seenCodes.has(code)) continue;
      seenCodes.add(code);
    }

    labels.push(toCodeLabel(condition));
  }

  return labels.join(", ");
};

const toUnitsLabel = (procedure: Procedure): string => {
  const units = getExtensionValue(procedure, Extensions.ProcedureUnits);

  return typeof units === "number" ? String(units) : "";
};

// A single Procedure can carry more than one modifier, each stored as its own repeated extension.
const toModifiersLabel = (procedure: Procedure): string =>
  (procedure.extension ?? [])
    .filter((extension) => extension.url === Extensions.ProcedureModifiers)
    .map((extension) => extension.valueString)
    .filter((modifier): modifier is string => Boolean(modifier))
    .join(", ");

/**
 * Flattens one side of the diff to display strings.
 *
 * A side with no version, or one whose version is retired, has nothing to show: the blank values are
 * what make a removal render as the previous side struck through against an empty amended side.
 *
 * @param procedure - The procedure as this side recorded it, if it existed.
 * @param liveConditionsById - The diagnoses live on this same side.
 * @returns The side's display values, blank throughout when the side has nothing recorded.
 */
const toProcedureValues = (
  procedure: Procedure | undefined,
  liveConditionsById: ReadonlyMap<string, Condition>,
): ProceduresAmendmentProcedureValues => {
  if (!procedure || isRetiredProcedure(procedure)) return BLANK_VALUES;

  return {
    code: toCodeLabel(procedure),
    units: toUnitsLabel(procedure),
    modifiers: toModifiersLabel(procedure),
    providerNotes: readProcedureNoteText(procedure, Extensions.ProcedureProviderNotes),
    billerNotes: readProcedureNoteText(procedure, Extensions.ProcedureBillerNotes),
    diagnoses: toDiagnosesLabel(procedure, liveConditionsById),
  };
};

const hasAnyValue = (values: ProceduresAmendmentProcedureValues): boolean =>
  Object.values(values).some((value) => value.length > 0);

const valuesMatch = (
  first: ProceduresAmendmentProcedureValues,
  second: ProceduresAmendmentProcedureValues,
): boolean =>
  (Object.keys(first) as (keyof ProceduresAmendmentProcedureValues)[]).every(
    (field) => first[field] === second[field],
  );

/**
 * What the amendment did to one procedure, or undefined when it left it alone.
 *
 * Read off the flattened values rather than the resources: a version bump that changed nothing the
 * tab shows — a category rebuilt with the same codings, a recorder filled in — is not a change the
 * amendment view has anything to say about.
 *
 * @param previous - The side the amendment found.
 * @param current - The side the amendment left behind.
 * @returns The change, or undefined when the two sides say the same thing.
 */
const resolveChange = (
  previous: ProceduresAmendmentProcedureValues,
  current: ProceduresAmendmentProcedureValues,
): ProceduresAmendmentChange | undefined => {
  const wasRecorded = hasAnyValue(previous);
  const isRecorded = hasAnyValue(current);

  if (!wasRecorded && !isRecorded) return undefined;
  if (!wasRecorded) return "added";
  if (!isRecorded) return "removed";

  return valuesMatch(previous, current) ? undefined : "modified";
};

/**
 * Builds one row per procedure the amendment changed.
 *
 * Each resource is read at both ends of the window, so a row describes what this one amendment did
 * rather than everything that has ever happened to the procedure. A resource the amendment never
 * touched reads the same on both sides and gets no row, which is what lets the section render nothing
 * for an amendment that left the procedures tab alone.
 *
 * @param window - The amendment's window, from `resolveProceduresAmendmentWindow`.
 * @param chains - Every version of every procedure and diagnosis the encounter holds.
 * @returns The changed procedures, in the order the encounter's read returned them.
 */
export const buildProceduresAmendmentRows = ({
  window,
  chains,
}: {
  window: ProceduresAmendmentWindow;
  chains: EncounterProceduresVersionChains;
}): ProceduresAmendmentRow[] => {
  const boundaries = toProceduresAmendmentBoundaries(window);
  if (!boundaries) return [];

  const conditionPairs = chains.conditionVersions.map((versions) =>
    pickProceduresAmendmentVersionPair(versions, boundaries),
  );
  const previousConditionsById = toLiveConditionsById(
    conditionPairs.map(({ previous }) => previous),
  );
  const currentConditionsById = toLiveConditionsById(conditionPairs.map(({ current }) => current));

  const rows: ProceduresAmendmentRow[] = [];

  for (const versions of chains.procedureVersions) {
    const { previous: previousProcedure, current: currentProcedure } =
      pickProceduresAmendmentVersionPair(versions, boundaries);

    const procedureId = currentProcedure?.id ?? previousProcedure?.id;
    if (!procedureId) continue;

    const previous = toProcedureValues(previousProcedure, previousConditionsById);
    const current = toProcedureValues(currentProcedure, currentConditionsById);

    const change = resolveChange(previous, current);
    if (!change) continue;

    rows.push({ procedureId, change, previous, current });
  }

  return rows;
};
